import * as React from 'react';
import { compose, withProps, branch, renderNothing } from 'recompose';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { State } from 'reducers';
import { CellIndex, Timeline, Marker, UtcTime, Camera, Stream } from 'types';
import { skin, OemProps } from 'ui/styles/skin';
import { Fade } from 'ui/transitions';
import { timelineClick } from 'actions/timelineActions';
import { setScroll, displayVideoError } from 'actions/monitoringActions';
import { getTimeline, getScroll, getStreamPosition, isPlaying, getCameraFromCell, getStreamFromCell, isLive } from 'selectors/monitoringSelectors';
import { getTimezoneOffsetCalculator } from 'selectors/serverSelectors';
import { TimelineSvg, TimelineState } from './TimelineSvg';
import { rasterizeBars } from './rasterizeBars';
import { postSeekRequest } from 'api/camera';

import './TimelineView.css';

const oneMinute = 60 * 1000;

const emptyTimeline: Timeline = {
    baseTime: 0,
    displayWidth: 0,
    displayHeight: 0,
    segmentWidth: 0,
    segmentHeight: 0,
    segmentMargin: 0,
    labelWidth: 0,
    minutesPerSegment: 30,
    bars: [],
    segments: 0,
    scale: 0.0001,
    markers: {},
};

type Props = MergeProps & OemProps;

export class RawTimelineView extends React.Component<Props, TimelineState> {
    container: HTMLDivElement;

    constructor(props: Props) {
        super(props);
        this.state = {
            tooltipImageUrl: null,
            hoverTime: null,
            hoverX: 0,
            hoverY: 0,
            hoverMarker: null,
            frameRequestTime: 0,
            markerFrameSrc: '',
            segmentImages: null,
        };
    }

    componentDidMount() {
        this.setScroll();
    }

    componentDidUpdate(oldProps: Props) {
        this.setScroll();

        const { timeline, styles } = this.props;
        if (oldProps.timeline.bars !== timeline.bars) {
            let segmentImages: string[] | null = null;
            try {
                // Would like to do this in a worker thread... but they can't access DOM elements
                // (i.e. canvas) and OffscreenCanvas is only supported by Firefox (and apparently
                // is buggy for the 2d context anyways)
                segmentImages = rasterizeBars(timeline, styles);
            }
            catch (err) {
                console.error(err);
            }

            this.setState(() => ({
                segmentImages,
            }));
        }
    }

    setScroll = () => {
        const height = this.container.scrollHeight;
        this.container.scrollTop = Math.round(this.props.scroll * height);
    }

    handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        e.stopPropagation();
        const scroll =
            e.currentTarget.scrollHeight
                ? e.currentTarget.scrollTop / e.currentTarget.scrollHeight
                : 1;
        this.props.onScroll(scroll);
    }

    //
    // Show a tooltip with the time when the user hovers over a segment of the timeline
    //
    handleMouseMove = (e: React.MouseEvent<SVGElement>, hoverMarker: Marker | null = null) => {
        this.setTooltipState(e.currentTarget, e.clientX, e.clientY, hoverMarker);
    }

    setTooltipState = (element: SVGElement | null, clientX: number, clientY: number, hoverMarker: Marker | null = null) => {
        if (!element) {
            return;
        }

        const { timeline } = this.props;
        const {
            baseTime,
            segmentWidth,
            segmentHeight,
            segmentMargin,
            labelWidth,
            minutesPerSegment,
            scale,
        } = timeline;

        const { top, left } = element.getBoundingClientRect();
        const x = Math.round(clientX - left - labelWidth);
        const y = Math.round(clientY - top - segmentMargin);

        let segment = Math.floor(y / (segmentHeight + segmentMargin));
        const yWithinSegment = y % (segmentHeight + segmentMargin);
        if (!hoverMarker && (y < 0 || yWithinSegment > segmentHeight || x < 0 || x >= segmentWidth)) {
            this.setState({
                frameRequestTime: 0,
                markerFrameSrc: '',
                hoverTime: null,
                hoverMarker,
            });
            return;
        }

        // If the user is hovering over a marker and the mouse pointer is in the gap between segments
        // then we need to increment the segment counter to the next line in order to get the correct time.
        // (because the top of the marker from one segment overlaps the margin from the previous segment)
        if (hoverMarker && yWithinSegment >= segmentHeight) {
            segment += 1;
        }

        const hoverTime = baseTime + (segment * minutesPerSegment * oneMinute) + x / scale;

        this.setState(state => ({
            frameRequestTime: hoverMarker ? state.frameRequestTime : 0,
            markerFrameSrc: hoverMarker ? state.markerFrameSrc : '',
            hoverTime,
            hoverX: x,
            hoverY: y,
            hoverMarker,
        }));
    }

    handleMouseLeave = () => {
        // Hide the tooltip when the user leaves the timeline area
        this.setState({
            hoverTime: null,
            hoverMarker: null,
            frameRequestTime: 0,
        });
    }

    utcTimeFromMarker = (marker: Marker): UtcTime => {
        const { timeline } = this.props;
        const { baseTime, scale } = timeline;
        return marker.position / scale + baseTime;
    }

    handleMouseOverMarker = (e: React.MouseEvent<SVGElement>, marker: Marker) => {
        // Prevent the MouseMove handler for the SVG element from firing
        // (because it will cause the tool tip window to hide)
        e.stopPropagation();

        const { stream } = this.props;
        const { deviceId, streamIndex } = stream!;
        const { clientX, clientY } = e;
        let { currentTarget } = e;
        let noChange = false;

        // Move up the DOM until we find the SVG node
        while (currentTarget && currentTarget.nodeName !== 'svg') {
            currentTarget = (currentTarget.parentNode as SVGElement)!;
        }

        const utcTime = this.utcTimeFromMarker(marker);
        this.setState(state => {
            if (utcTime === state.frameRequestTime) {
                noChange = true;
                return state;
            }
            return {
                ...state, // Not required by React... but the current type declarations only accept State, not Partial<State>
                frameRequestTime: utcTime,
                markerFrameSrc: '',
            };
        }, async () => {
            if (noChange) {
                return;
            }

            // Cause the tooltip to be drawn
            this.setTooltipState(currentTarget as SVGElement, clientX, clientY, marker);

            let alarmFrame: string;
            let frameRequestTime = utcTime; // Start as the same time we're about to request
            try {
                // Request the alarm image from the server
                const { frame } = await postSeekRequest(deviceId, streamIndex, 'to', utcTime, false);
                alarmFrame = frame;
            }
            catch (err) {
                alarmFrame = '';
                frameRequestTime = 0; // Set to zero to show the tooltip with just the time; no image.
                this.props.onAlarmImageError(err);
            }

            this.setState(state => {
                // If the frameRequestTime changed then another request has been made since this one was
                // first issued... so drop the frame we just received. At least the browser will have
                // cached it in case the user requests it again
                if (state.frameRequestTime !== utcTime) {
                    noChange = true;
                    return state;
                }
                return {
                    ...state, // Not required by React... but the current type declarations only accept State, not Partial<State>
                    frameRequestTime,
                    markerFrameSrc: alarmFrame,
                };
            }, () => {
                if (noChange) {
                    return;
                }
   
                // Cause the tooltip to be drawn
                this.setTooltipState(currentTarget as SVGElement, clientX, clientY, marker);
            });
        });
    }

    handleMouseOutMarker = (e: React.MouseEvent<SVGElement>, marker: Marker) => {
        // Remove the alarm image from the tooltip when the user's cursor leaves the alarm
        this.setState({
            tooltipImageUrl: null,
        });
    }

    render() {
        const {
            cellIndex,
            timeline,
            styles,
        } = this.props;
        const { scroll, ...otherProps } = this.props;

        return (
            <Fade visible={ !!timeline.baseTime } duration={ 200 }>
                <div
                    ref={ _ => this.container = _ as HTMLDivElement }
                    className="timeline"
                    id={ `timeline${cellIndex}` }
                    style={ styles.Timeline }
                    onScroll={ this.handleScroll }
                >
                    <TimelineSvg
                        onMouseMove={ this.handleMouseMove }
                        onMouseLeave={ this.handleMouseLeave }
                        onMouseOverMarker={ this.handleMouseOverMarker }
                        onMouseOutMarker={ this.handleMouseOutMarker }
                        { ...this.state }
                        { ...otherProps }
                    />
                </div>
            </Fade>
        );
    }
}


export type OwnProps = {
    cellIndex: CellIndex;
};
type StateProps = {
    canView: boolean;
    camera: Camera | null;
    stream: Stream | null;
    timeline: Timeline;
    tzOffsetCalculator: (utcTime: UtcTime) => number;
    streamPosition: UtcTime;
    playing: boolean;
    scroll: number;
};
type DispProps = {
    onScroll: (n: number) => void;
    onClickMarker: (timeline: Timeline) => (marker: Marker) => void;
    onClickTimeline: (time: UtcTime) => void;
    onAlarmImageError: (err: Error) => void;
};
type MergeProps = StateProps & OwnProps & {
    onScroll: (n: number) => void;
    onClickMarker: (marker: Marker) => void;
    onClickTimeline: (time: UtcTime) => void;
    onAlarmImageError: (err: Error) => void;
};
type RecomposeProps = {
    styles: { [name: string]: React.CSSProperties };
};

const mapStateToProps = (state: State, ownProps: OwnProps): StateProps => {
    const live = isLive(state, ownProps);
    const camera = getCameraFromCell(state, ownProps);
    const canView =
        !camera ||
        (
            (live && camera.canViewLive) ||
            (!live && camera.canViewHistorical)
        );

    return {
        canView,
        camera,
        stream: getStreamFromCell(state, ownProps),
        timeline: getTimeline(state, ownProps) || emptyTimeline,
        tzOffsetCalculator: getTimezoneOffsetCalculator(state),
        streamPosition: getStreamPosition(state, ownProps),
        playing: isPlaying(state, ownProps),
        scroll: getScroll(state, ownProps),
    };
};

const mapDispatchToProps = (dispatch: Function, { cellIndex, t }: OwnProps & TranslateProps): DispProps => ({
    onScroll: scroll => {
        dispatch(setScroll({ scroll, cellIndex }));
    },
    onClickMarker: timeline => marker => {
        const utcTime = timeline.baseTime + (marker.position / timeline.scale);
        dispatch(timelineClick({ cellIndex, utcTime }));
    },
    onClickTimeline: (utcTime: UtcTime) => {
        dispatch(timelineClick({ cellIndex, utcTime }));
    },
    onAlarmImageError: (err: Error) => {
        const localError = new Error(t(err.message));
        dispatch(displayVideoError(localError, { cellIndex }));
    },
});

const mergeProps = (stateProps: StateProps, { onClickMarker, ...dispProps }: DispProps, ownProps: OwnProps): MergeProps => ({
    ...stateProps,
    ...dispProps,
    onClickMarker: onClickMarker(stateProps.timeline),
    ...ownProps,
});

export const TimelineView = compose<MergeProps & RecomposeProps, OwnProps>(
    skin,
    translate(),
    withProps(({ styles }: OemProps) => ({
        styles: {
            ...styles,
            foreground20: {
                fill: styles.Window.color,
                fillOpacity: .2,
            },
            foreground75: {
                fill: styles.Window.color,
                opacity: .75,
            },
            background80: {
                fill: styles.Window.backgroundColor,
                fillOpacity: .8,
            },
        },
    })),
    connect(mapStateToProps, mapDispatchToProps, mergeProps),
    branch<StateProps>(({ canView }) => !canView, renderNothing),
)(RawTimelineView);



// WEBPACK FOOTER //
// ./src/ui/pages/monitoring/mainPanel/timeline/TimelineView.tsx